import { z } from 'zod';
import { ABN_REGEX, AU_PHONE_REGEX } from './constants.js';
/*
 * Optional fields from both HTML forms (empty string) and JSON API requests (absent key)
 * must coerce to undefined. z.string().optional() as the base accepts both string and
 * undefined inputs — absent keys in parsed JSON arrive as undefined and pass validation.
 * The transform then coerces "" → undefined before piping to any inner validator.
 */
/**
 * Converts empty strings to `undefined`. Used with `.transform(emptyToUndefined)` on
 * optional string fields.
 */
export const emptyToUndefined = (val) => val === '' ? undefined : val;
/**
 * Optional string field that accepts absent keys and coerces empty strings to
 * `undefined`. Input type is `string | undefined` — suitable for both form fields and API
 * requests where optional fields may be omitted.
 */
export const optionalStr = z
    .string()
    .optional()
    .transform(emptyToUndefined)
    .pipe(z.string().optional());
/**
 * Optional email field with input type `string` (not `string | undefined`) — for use as a
 * TanStack Form field validator, where field values are always initialized to `''`, never
 * `undefined`. Coerces empty strings to `undefined` before validating format. API schemas
 * that also accept an absent key should use `optionalEmail.optional()`.
 */
export const optionalEmail = z
    .string()
    .trim()
    .transform(emptyToUndefined)
    .pipe(z.email().optional());
/**
 * Optional AU phone field with input type `string | undefined`, mirroring
 * {@link optionalStr} — coerces empty strings to `undefined`, then validates against
 * {@link AU_PHONE_REGEX} with a 50-character cap. Absent/blank phone stays valid.
 */
export const optionalPhone = z
    .string()
    .optional()
    .transform(emptyToUndefined)
    .pipe(z
    .string()
    .max(50)
    .regex(AU_PHONE_REGEX, 'Must be a valid Australian phone number')
    .optional());
export const optionalPhoneField = z
    .string()
    .transform(emptyToUndefined)
    .pipe(z
    .string()
    .max(50)
    .regex(AU_PHONE_REGEX, 'Must be a valid Australian phone number')
    .optional());
/**
 * Optional ABN field with input type `string | undefined`, mirroring {@link optionalStr}
 * — coerces empty strings to `undefined`, then validates against {@link ABN_REGEX}.
 * Absent/blank value stays valid; format only, no checksum validation.
 */
export const optionalAbn = z
    .string()
    .optional()
    .transform(emptyToUndefined)
    .pipe(z.string().regex(ABN_REGEX, 'Must be a valid ABN (e.g. 12 345 678 901)').optional());
/**
 * Optional ABN field with input type `string` (not `string | undefined`) — for use as a
 * TanStack Form field validator, mirroring {@link optionalPhoneField}.
 */
export const optionalAbnField = z
    .string()
    .transform(emptyToUndefined)
    .pipe(z.string().regex(ABN_REGEX, 'Must be a valid ABN (e.g. 12 345 678 901)').optional());
/**
 * Tiptap serializes "no content" as `<p></p>` (or `<p><br></p>` after certain edits),
 * never as `''` — a plain truthiness check on the HTML string treats those as non-empty.
 * Also strips `&nbsp;`/non-breaking-space entities Tiptap can leave behind on an
 * otherwise-empty paragraph, which `.trim()` alone would not collapse.
 */
export function isRichTextEmpty(content) {
    if (!content)
        return true;
    return (content
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, '')
        .replace(/\u00A0/g, '')
        .trim().length === 0);
}
//# sourceMappingURL=utils.js.map